/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import React, { useState } from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export type SavingState = 'dirty' | 'inProgress' | 'saved';

export type SavingIndicatorData = {
    savingState: SavingState;
    onSave: () => void;
};

export const useSaveChanges = (autoSaveEnabled = !FeatureToggles.CustomTextGptLabeling): SavingIndicatorData => {
    const { dispatcher, rootStore } = useGlobalState();

    const handleSaveDocumentStores = () => {
        dispatcher.dispatch('saveDocumentStoresChanges', { tracker: savingCurrentDocumentStoreTracker });
    };

    const tracker = useProgressTracker();
    const savingCurrentDocumentStoreTracker = useProgressTracker({
        onComplete: () => {
            dispatcher.dispatch('updateCustomTextProjectAssets', { tracker, toastData: {} });
        }
    });
    const savingGptStoreTracker = useProgressTracker({ onComplete: handleSaveDocumentStores });

    const { isDirty, gptPredictionsStore } = rootStore.currentCustomTextProjectStore;
    const { isDirty: isGptDecisionsDirty } = gptPredictionsStore;
    const isInProgress = tracker.isRunning;
    const [savingState, setSavingState] = useState<SavingState>(isDirty ? 'dirty' : 'saved');

    const getCurrentState = (): SavingState => {
        if (isInProgress) {
            return 'inProgress';
        }
        if (isDirty || isGptDecisionsDirty) {
            return 'dirty';
        }
        return 'saved';
    };

    const handleOnSave = React.useCallback(() => {
        if (isGptDecisionsDirty) {
            dispatcher.dispatch('submitGptDecisions', { toastData: {}, tracker: savingGptStoreTracker });

            return;
        }

        if (isDirty) {
            handleSaveDocumentStores();
        }
    }, [isDirty, tracker, isGptDecisionsDirty]);

    React.useEffect(() => {
        if ((isDirty || isGptDecisionsDirty) && autoSaveEnabled) {
            const timeout = setTimeout(handleOnSave, LuisConstants.CustomText.SyncingLabelsTimeout);

            return () => clearTimeout(timeout);
        }
    }, [isDirty, autoSaveEnabled, isGptDecisionsDirty]);

    React.useEffect(() => {
        setSavingState(getCurrentState());
    }, [isDirty, isGptDecisionsDirty, isInProgress]);

    return { savingState, onSave: handleOnSave };
};
